/**
 * Project: Aniatek
 * Author: Carlos A. Loaiza | Connect & Launch
 * Tel: +52 (667) 247 - 6316
 * Intellectual Property: All rights reserved (c) 2026
 */
 
 <template>
  <div class="paso-cinco-container">

    <div class="main-user-view">
      <div class="confirmation-header">
        <h2>Resumen del Proyecto: <strong>{{ formData?.nombreProyecto }}</strong></h2>
      </div>
      <div class="confirmation-content">
        <GridMetricas :formData="formData" :environmentalBenefit="environmentalBenefit" :tirRoi="tirRoi"
          :totalCost25Years="totalCost25Years" :averagePago="averagePago" :averageKwh="averageKwh"
          :roiDetail="roiDetail" />
        <TablaHistorico :consumoHistorico="historicoFormateado" />
        <TablaRetorno :roiDetail="roiDetail" />
        <DesgloseCostos :quoteTotals="quoteTotals" />

        <BarraAcciones @descargar-pdf="generarPDF" />
      </div>
    </div>

    <div class="pdf-zero-flow-wrapper">
      <div ref="reporteCotizacion">
        <component :is="componentePdfActual" v-if="generandoPdf" :data="pdfData" />
      </div>
    </div>

    <div v-if="generandoPdf" class="pdf-loading-screen">
      <div class="loader-box">
        <div class="spinner-blue"></div>
        <h3>Generando Cotización...</h3>
        <p>Esto puede tardar unos segundos</p>
      </div>
    </div>
  </div>
</template>

<script>
import html2pdf from 'html2pdf.js';
import GridMetricas from './GridMetricas.vue';
import TablaHistorico from './TablaHistorico.vue';
import TablaRetorno from './TablaRetorno.vue';
import DesgloseCostos from './DesgloseCostos.vue';
import BarraAcciones from './BarraAcciones.vue';
import PDFCliente from './PDFCliente.vue';
import PDFInstalador from './PDFInstalador.vue';

export default {
  name: 'PasoCincoIndex',
  mounted() {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  },
  components: {
    GridMetricas, TablaHistorico, TablaRetorno, DesgloseCostos, BarraAcciones,
    PDFCliente, PDFInstalador
  },
  props: {
    formData: Object,
    consumoHistorico: Array,
    roiDetail: Array,
    quoteTotals: Object,
    tirRoi: Object,
    totalKwhHistorico: Number,
    totalPagoHistorico: Number,
    averagePago: Number,
    averageKwh: Number,
    environmentalBenefit: Object,
    totalCost25Years: Object
  },
  data() {
    return {
      generandoPdf: false,
      componentePdfActual: null
    };
  },
  computed: {
    ahorroCalculado() {
      if (this.roiDetail && this.roiDetail.length > 0) {
        const ahorroAnualAño1 = Number(this.roiDetail[0].ahorro || this.roiDetail[0].savings) || 0;

        if (ahorroAnualAño1 > 0) {
          return Math.max(0, ahorroAnualAño1 / 12);
        }
      }

      return Math.max(0, this.averagePago - this.nuevoPagoMensual);
    },
    historicoFormateado() {
      const nombresMeses = [
        "Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio",
        "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre"
      ];

      const traducir = (valor) => {
        const num = parseInt(valor);
        return (num >= 1 && num <= 12) ? nombresMeses[num - 1] : valor;
      };

      return (this.consumoHistorico || []).map(item => ({
        ...item,
        inicio: traducir(item.inicio),
        termino: traducir(item.termino)
      }));
    },

    pdfData() {
      return {
        formData: this.formData || {},
        consumoHistorico: this.historicoFormateado || [],
        roiDetail: this.roiDetail || [],
        quoteTotals: this.quoteTotals || {},
        tirRoi: this.tirRoi || {},
        totalKwhHistorico: this.totalKwhHistorico || 0,
        totalPagoHistorico: this.totalPagoHistorico || 0,
        averagePago: this.averagePago || 0,
        averageKwh: this.averageKwh || 0,
        environmentalBenefit: this.environmentalBenefit || {},
        totalCost25Years: this.totalCost25Years || {},
        ahorroMensual: this.ahorroCalculado
      };
    }
  },
  methods: {
    async generarPDF(tipo) {
      this.generandoPdf = true;
      this.componentePdfActual = (tipo === 'cliente') ? 'PDFCliente' : 'PDFInstalador';
      await this.$nextTick();
      await new Promise(resolve => setTimeout(resolve, 1200));

      const element = this.$refs.reporteCotizacion;
      const options = {
        margin: 0,
        filename: `Cotizacion_${tipo}.pdf`,
        image: { type: 'jpeg', quality: 0.98 },
        html2canvas: {
          scale: 2,
          useCORS: true,
          windowHeight: element.scrollHeight
        },
        jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' },
        pagebreak: {
          mode: ['css', 'legacy'], 
          after: '.pdf-page-container' 
        }
      };

      try {
        await html2pdf().set(options).from(element).save();
      } catch (error) {
        console.error("Error al generar el PDF:", error);
      } finally {
        this.generandoPdf = false;
        this.componentePdfActual = null;
      }
    }
  }
};
</script>

<style scoped>
.paso-cinco-container {
  width: 100%;
  position: relative;
}

.main-user-view {
  padding: 20px;
  margin: 0 auto;
}

.confirmation-header h2 {
  font-size: 24px;
  color: #1a202c;
  margin-bottom: 25px;
}

.pdf-zero-flow-wrapper {
  position: absolute;
  left: -10000px;
  top: 0;
  width: 210mm;
  height: 0;
  overflow: hidden;
}

.pdf-loading-screen {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(255, 255, 255, 0.92);
  z-index: 9999;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
}

.loader-box {
  text-align: center;
}

.loader-box h3 {
  color: #29b9f6;
  margin-top: 15px;
  margin-bottom: 5px;
}

.loader-box p {
  color: #718096;
  font-size: 14px;
}

.spinner-blue {
  width: 50px;
  height: 50px;
  border: 5px solid #f3f3f3;
  border-top: 5px solid #29b9f6;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin: 0 auto;
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }

  100% {
    transform: rotate(360deg);
  }
}
</style>